import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { v4 as uuidv4 } from 'uuid';
import {
    ExternalLink,
    MessageSquareText,
    Pencil,
    Plus,
    Settings,
    Trash2,
    Zap,
} from 'lucide-react';

import OverrideField from './OverrideField';
import CreateStarter from '../builder/CreateStarter';
import Links from '../builder/Links';
import { AgentAppOverrides } from './appOverrides';
import AgentTabHeader from './AgentTabHeader';

export interface AppConfigurationTabProps {
    agId?: string;
    agentName: string;
    setNeedsUpdate: (v: boolean) => void;

    // Per-mode override state
    appOverrides: AgentAppOverrides;
    setAppOverrides: (updater: (prev: AgentAppOverrides) => AgentAppOverrides) => void;

    // Website base values (used for inherit previews)
    baseWelcomeMessage: string;
    baseStarters: any[];
    baseLinks: any[];
    baseSearchBarPlaceholder: string;
    basePopupMessage: string;
    basePopupMessageEnabled: boolean;
    baseContextualPopupEnabled: boolean;
    baseAutoOpenEnabled: boolean;
    baseAutoOpenSeconds: number;
    baseOpenOncePerSession: boolean;

    // App URL / widget settings — these are not per-mode overrides, they are
    // the settings that DEFINE when in-app mode is active.
    onboardingUrl: string;
    setOnboardingUrl: (v: string) => void;
    onboardingAllowlistUrls: string[];
    setOnboardingAllowlistUrls: (v: string[]) => void;
    onlyShowForAdmin: boolean;
    setOnlyShowForAdmin: (v: boolean) => void;
}

// Carry the admin flag in the URL hash rather than the query string. The hash is
// never sent to the server, so it survives the server-side redirects that strip
// query params before our embed script gets a chance to read them.
function appendHashParam(rawUrl: string, key: string, value: string) {
    const trimmed = (rawUrl || '').trim();
    if (!trimmed) return '';

    const pair = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;

    try {
        const url = new URL(trimmed);
        const existingHash = url.hash.replace(/^#/, '');
        url.hash = existingHash ? `${existingHash}&${pair}` : pair;
        return url.toString();
    } catch {
        const hashIndex = trimmed.indexOf('#');
        if (hashIndex === -1) return `${trimmed}#${pair}`;
        const hasHashContent = hashIndex < trimmed.length - 1;
        return `${trimmed}${hasHashContent ? '&' : ''}${pair}`;
    }
}

export default function AppConfigurationTab({
    agId,
    agentName,
    setNeedsUpdate,
    appOverrides,
    setAppOverrides,
    baseWelcomeMessage,
    baseStarters,
    baseLinks,
    baseSearchBarPlaceholder,
    basePopupMessage,
    basePopupMessageEnabled,
    baseContextualPopupEnabled,
    baseAutoOpenEnabled,
    baseAutoOpenSeconds,
    baseOpenOncePerSession,
    onboardingUrl,
    setOnboardingUrl,
    onboardingAllowlistUrls,
    setOnboardingAllowlistUrls,
    onlyShowForAdmin,
    setOnlyShowForAdmin,
}: AppConfigurationTabProps) {
    const [starterModalVisible, setStarterModalVisible] = useState(false);
    const [activeStarter, setActiveStarter] = useState<any>(null);

    const recordTasksUrl = appendHashParam(onboardingUrl, 'aimdocAdmin', 'true');
    const canRecordTasks = Boolean(recordTasksUrl);

    const handleAllowlistUrlChange = (index: number, nextValue: string) => {
        setOnboardingAllowlistUrls(
            onboardingAllowlistUrls.map((url, i) => (i === index ? nextValue : url))
        );
    };

    const handleAddAllowlistUrl = () => {
        setOnboardingAllowlistUrls([...onboardingAllowlistUrls, '']);
    };

    const handleRemoveAllowlistUrl = (index: number) => {
        setOnboardingAllowlistUrls(onboardingAllowlistUrls.filter((_, i) => i !== index));
    };

    // ---- Override helpers ----------------------------------------------------

    const overrideWelcome = appOverrides.welcomeMessage ?? null;
    const isWelcomeOverridden = overrideWelcome !== null && overrideWelcome !== undefined;

    const overrideStarters = appOverrides.starters ?? null;
    const isStartersOverridden = overrideStarters !== null && overrideStarters !== undefined;

    const overrideLinks = appOverrides.links ?? null;
    const isLinksOverridden = overrideLinks !== null && overrideLinks !== undefined;

    const overrideSearchBarPlaceholder = appOverrides.searchBarPlaceholder ?? null;
    const isSearchBarPlaceholderOverridden =
        overrideSearchBarPlaceholder !== null && overrideSearchBarPlaceholder !== undefined;

    const overridePopupMessage = appOverrides.popupMessage ?? null;
    const overridePopupEnabled = appOverrides.popupMessageEnabled ?? null;
    const isPopupOverridden =
        (overridePopupMessage !== null && overridePopupMessage !== undefined)
        || (overridePopupEnabled !== null && overridePopupEnabled !== undefined);

    const overrideAutoOpenEnabled = appOverrides.autoOpenEnabled ?? null;
    const overrideAutoOpenSeconds = appOverrides.autoOpenSeconds ?? null;
    const overrideOpenOnce = appOverrides.openOncePerSession ?? null;
    const isAutoOpenOverridden =
        (overrideAutoOpenEnabled !== null && overrideAutoOpenEnabled !== undefined)
        || (overrideAutoOpenSeconds !== null && overrideAutoOpenSeconds !== undefined)
        || (overrideOpenOnce !== null && overrideOpenOnce !== undefined);

    const removeKeys = (keys: (keyof AgentAppOverrides)[]) =>
        setAppOverrides((prev) => {
            const next = { ...prev };
            for (const key of keys) {
                delete (next as any)[key];
            }
            return next;
        });

    // ---- Starters helpers (when overridden) ---------------------------------

    const currentStarters = overrideStarters ?? [];
    const currentLinks = overrideLinks ?? [];

    const setOverrideLinks = (valueOrUpdater: any) => {
        setAppOverrides((prev) => {
            const previousLinks = Array.isArray(prev.links) ? prev.links : [];
            const nextLinks =
                typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(previousLinks)
                    : valueOrUpdater;
            return {
                ...prev,
                links: Array.isArray(nextLinks) ? nextLinks : [],
            };
        });
    };

    const addStarter = (starterValue: string) => {
        if (!starterValue) return false;
        if (currentStarters.length >= 3) return false;
        const next = [...currentStarters, { id: uuidv4(), value: starterValue }];
        setAppOverrides((prev) => ({ ...prev, starters: next }));
        setNeedsUpdate(true);
        return true;
    };

    const editStarter = (id: string, starterValue: string) => {
        const next = currentStarters
            .filter((s: any) => s.id !== id)
            .concat([{ id, value: starterValue }]);
        setAppOverrides((prev) => ({ ...prev, starters: next }));
        setNeedsUpdate(true);
    };

    const deleteStarter = (id: string) => {
        const next = currentStarters.filter((s: any) => s.id !== id);
        setAppOverrides((prev) => ({ ...prev, starters: next }));
        setNeedsUpdate(true);
    };

    const toggleStarterModal = (starter?: any) => {
        if (starterModalVisible) setActiveStarter(null);
        else setActiveStarter(starter);
        setStarterModalVisible(!starterModalVisible);
    };

    return (
        <div className="w-full">
            <AgentTabHeader
                icon={Settings}
                title="App mode configuration"
                description="Define where app mode runs and customize engagement for signed-in users."
            />

            <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
            {/* -------- App URLs (gating) -------- */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">App URLs</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Choose the pages where the widget switches into app mode.
                    </p>
                </div>

                <div className="rounded-lg border bg-card p-4">
                        <div className="min-w-0 space-y-4">
                            <div>
                                <p className="text-sm font-medium">Main App URL</p>
                                <p className="text-xs text-muted-foreground">
                                    This URL triggers app mode and is used for URL-based matching.
                                </p>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="onboarding-main-url" className="sr-only">
                                    Main App URL
                                </Label>
                                <Input
                                    id="onboarding-main-url"
                                    type="url"
                                    placeholder="https://app.example.com"
                                    value={onboardingUrl}
                                    onChange={(e) => setOnboardingUrl(e.target.value)}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        if (!recordTasksUrl) return;
                                        window.open(
                                            recordTasksUrl,
                                            '_blank',
                                            'noopener,noreferrer'
                                        );
                                    }}
                                    disabled={!canRecordTasks}
                                >
                                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                    Record Tasks
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setNeedsUpdate(true)}
                                >
                                    Save Draft
                                </Button>
                            </div>

                            <div className="space-y-3 border-t pt-3">
                                <div>
                                    <p className="text-sm font-medium">URL Allow List</p>
                                    <p className="text-xs text-muted-foreground">
                                        App mode runs when the current page matches the Main App URL or a
                                        URL below (host + path prefix).
                                    </p>
                                </div>

                                {onboardingAllowlistUrls.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">
                                        No allow-list URLs added yet.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {onboardingAllowlistUrls.map((allowUrl, index) => (
                                            <div
                                                key={`allowlist-url-${index}`}
                                                className="flex items-center gap-2"
                                            >
                                                <Label
                                                    htmlFor={`allowlist-url-${index}`}
                                                    className="sr-only"
                                                >
                                                    Allow-list URL {index + 1}
                                                </Label>
                                                <Input
                                                    id={`allowlist-url-${index}`}
                                                    type="url"
                                                    placeholder="https://staging.app.example.com/app"
                                                    value={allowUrl}
                                                    onChange={(e) =>
                                                        handleAllowlistUrlChange(
                                                            index,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveAllowlistUrl(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddAllowlistUrl}
                                >
                                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                                    Add URL
                                </Button>
                            </div>
                    </div>
                </div>
            </div>

            {/* -------- Widget Settings -------- */}
            <div className="space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Widget settings</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Control who can access the widget in app mode.
                    </p>
                </div>

                <div className="rounded-lg border bg-card p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="space-y-1">
                            <Label
                                htmlFor="only-show-for-admin"
                                className="text-sm font-medium cursor-pointer"
                            >
                                Only show for admin
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                When enabled, the widget is hidden for non-admins only while app mode is
                                active.
                            </p>
                        </div>
                        <Switch
                            id="only-show-for-admin"
                            checked={onlyShowForAdmin}
                            onCheckedChange={setOnlyShowForAdmin}
                        />
                    </div>
                </div>
            </div>

            {/* -------- Engagement Overrides -------- */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold">Engagement</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Inherit website settings or customize them for app mode.
                        </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">
                        Optional overrides
                    </Badge>
                </div>

                {/* Welcome message */}
                <OverrideField
                    label="Welcome message"
                    description="First greeting shown when the widget opens in app mode."
                    isOverridden={isWelcomeOverridden}
                    onCustomize={() =>
                        setAppOverrides((prev) => ({
                            ...prev,
                            welcomeMessage: baseWelcomeMessage ?? '',
                        }))
                    }
                    onReset={() => removeKeys(['welcomeMessage'])}
                    renderBasePreview={() => (
                        <span>{baseWelcomeMessage?.trim() || '(empty)'}</span>
                    )}
                >
                    <Textarea
                        value={overrideWelcome ?? ''}
                        onChange={(e) =>
                            setAppOverrides((prev) => ({
                                ...prev,
                                welcomeMessage: e.target.value,
                            }))
                        }
                        placeholder="Welcome back — how can I help you today?"
                        className="resize-y"
                        rows={3}
                    />
                </OverrideField>

                {/* Input placeholder */}
                <OverrideField
                    label="Input Bar Placeholder"
                    description="Placeholder text shown in the collapsed widget input bar in app mode."
                    isOverridden={isSearchBarPlaceholderOverridden}
                    onCustomize={() =>
                        setAppOverrides((prev) => ({
                            ...prev,
                            searchBarPlaceholder: baseSearchBarPlaceholder ?? '',
                        }))
                    }
                    onReset={() => removeKeys(['searchBarPlaceholder'])}
                    renderBasePreview={() => (
                        <span>{baseSearchBarPlaceholder?.trim() || '(empty)'}</span>
                    )}
                >
                    <Input
                        value={overrideSearchBarPlaceholder ?? ''}
                        onChange={(e) =>
                            setAppOverrides((prev) => ({
                                ...prev,
                                searchBarPlaceholder: e.target.value,
                            }))
                        }
                        placeholder="Enter input bar placeholder..."
                        maxLength={40}
                    />
                </OverrideField>

                {/* Links */}
                <OverrideField
                    label="Links"
                    description="Links shown at the top of the chat in app mode."
                    isOverridden={isLinksOverridden}
                    onCustomize={() =>
                        setAppOverrides((prev) => ({
                            ...prev,
                            links: Array.isArray(baseLinks)
                                ? baseLinks.map((link) => ({ ...link }))
                                : [],
                        }))
                    }
                    onReset={() => removeKeys(['links'])}
                    renderBasePreview={() =>
                        baseLinks && baseLinks.length > 0 ? (
                            <ul className="space-y-1">
                                {baseLinks.map((link: any) => (
                                    <li key={link.id}>• {link.name}</li>
                                ))}
                            </ul>
                        ) : (
                            <span>(No links set for website mode)</span>
                        )
                    }
                >
                    <Links
                        links={currentLinks}
                        setLinks={setOverrideLinks}
                        setNeedsUpdate={setNeedsUpdate}
                        agId={agId}
                        agentName={agentName}
                    />
                </OverrideField>

                {/* Starters */}
                <OverrideField
                    label="Starters"
                    description="App mode starters fully replace the website mode starters when customized."
                    isOverridden={isStartersOverridden}
                    onCustomize={() =>
                        setAppOverrides((prev) => ({
                            ...prev,
                            starters: Array.isArray(baseStarters) ? [...baseStarters] : [],
                        }))
                    }
                    onReset={() => removeKeys(['starters'])}
                    renderBasePreview={() =>
                        baseStarters && baseStarters.length > 0 ? (
                            <ul className="space-y-1">
                                {baseStarters.map((s: any) => (
                                    <li key={s.id}>• {s.value}</li>
                                ))}
                            </ul>
                        ) : (
                            <span>(No starters set for website mode)</span>
                        )
                    }
                >
                    <div className="space-y-2">
                        {currentStarters.length > 0 && (
                            <div className="overflow-hidden rounded-md border bg-background divide-y">
                                {currentStarters.map((starter: any) => (
                                    <div
                                        key={starter.id}
                                        className="group flex min-h-11 items-center gap-2.5 px-3 py-2 transition-colors hover:bg-muted/30"
                                    >
                                        <MessageSquareText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                        <p
                                            className="flex-1 text-sm cursor-pointer truncate"
                                            onClick={() => toggleStarterModal(starter)}
                                        >
                                            {starter.value}
                                        </p>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => toggleStarterModal(starter)}
                                            >
                                                <Pencil className="h-3 w-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 hover:text-destructive"
                                                onClick={() => deleteStarter(starter.id)}
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {currentStarters.length < 3 && (
                            <button
                                onClick={() => toggleStarterModal()}
                                className="w-full rounded-lg border border-dashed p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors cursor-pointer"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                Add app mode starter{' '}
                                {currentStarters.length > 0 && `(${currentStarters.length}/3)`}
                            </button>
                        )}
                    </div>
                </OverrideField>

                {/* Popup */}
                <OverrideField
                    label="Proactive popup"
                    description="Message shown from the closed widget to encourage engagement."
                    isOverridden={isPopupOverridden}
                    onCustomize={() =>
                        setAppOverrides((prev) => ({
                            ...prev,
                            popupMessage: basePopupMessage ?? '',
                            popupMessageEnabled: basePopupMessageEnabled,
                            contextualPopupEnabled: baseContextualPopupEnabled,
                        }))
                    }
                    onReset={() =>
                        removeKeys([
                            'popupMessage',
                            'popupMessageEnabled',
                            'contextualPopupEnabled',
                        ])
                    }
                    renderBasePreview={() => (
                        <div className="space-y-1">
                            <div>
                                Enabled: {basePopupMessageEnabled ? 'yes' : 'no'}
                            </div>
                            <div>Message: {basePopupMessage?.trim() || '(empty)'}</div>
                        </div>
                    )}
                >
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="in-app-popup-enabled" className="text-sm">
                                Show popup in app mode
                            </Label>
                            <Switch
                                id="in-app-popup-enabled"
                                checked={overridePopupEnabled ?? false}
                                onCheckedChange={(v) =>
                                    setAppOverrides((prev) => ({
                                        ...prev,
                                        popupMessageEnabled: v,
                                    }))
                                }
                            />
                        </div>
                        <Textarea
                            value={overridePopupMessage ?? ''}
                            onChange={(e) =>
                                setAppOverrides((prev) => ({
                                    ...prev,
                                    popupMessage: e.target.value,
                                }))
                            }
                            placeholder="Need help with your setup?"
                            rows={2}
                        />
                    </div>
                </OverrideField>

                {/* Auto-open */}
                <OverrideField
                    label="Auto-open"
                    description="Automatically open the widget after a delay for app mode users."
                    isOverridden={isAutoOpenOverridden}
                    onCustomize={() =>
                        setAppOverrides((prev) => ({
                            ...prev,
                            autoOpenEnabled: baseAutoOpenEnabled,
                            autoOpenSeconds: baseAutoOpenSeconds,
                            openOncePerSession: baseOpenOncePerSession,
                        }))
                    }
                    onReset={() =>
                        removeKeys(['autoOpenEnabled', 'autoOpenSeconds', 'openOncePerSession'])
                    }
                    renderBasePreview={() => (
                        <div className="space-y-1">
                            <div>Enabled: {baseAutoOpenEnabled ? 'yes' : 'no'}</div>
                            <div>Delay: {baseAutoOpenSeconds ?? 0}s</div>
                            <div>
                                Once per session: {baseOpenOncePerSession ? 'yes' : 'no'}
                            </div>
                        </div>
                    )}
                >
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="in-app-auto-open" className="text-sm">
                                Auto-open enabled
                            </Label>
                            <Switch
                                id="in-app-auto-open"
                                checked={overrideAutoOpenEnabled ?? false}
                                onCheckedChange={(v) =>
                                    setAppOverrides((prev) => ({
                                        ...prev,
                                        autoOpenEnabled: v,
                                    }))
                                }
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Label htmlFor="in-app-auto-open-seconds" className="text-sm">
                                Delay
                            </Label>
                            <Input
                                id="in-app-auto-open-seconds"
                                type="number"
                                min={0}
                                className="w-24"
                                value={overrideAutoOpenSeconds ?? 0}
                                onChange={(e) =>
                                    setAppOverrides((prev) => ({
                                        ...prev,
                                        autoOpenSeconds: Number(e.target.value) || 0,
                                    }))
                                }
                            />
                            <span className="text-xs text-muted-foreground">seconds</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <Label htmlFor="in-app-open-once" className="text-sm">
                                Only open once per session
                            </Label>
                            <Switch
                                id="in-app-open-once"
                                checked={overrideOpenOnce ?? false}
                                onCheckedChange={(v) =>
                                    setAppOverrides((prev) => ({
                                        ...prev,
                                        openOncePerSession: v,
                                    }))
                                }
                            />
                        </div>
                    </div>
                </OverrideField>
            </div>

            {/* Starter modal (shared between add/edit for overrides) */}
            {starterModalVisible && (
                <CreateStarter
                    addStarter={addStarter}
                    editStarter={editStarter}
                    setModalVisible={setStarterModalVisible}
                    starter={activeStarter}
                />
            )}

            {/* Hint when no URLs have been set */}
            {!onboardingUrl && onboardingAllowlistUrls.length === 0 && (
                <div className="rounded-lg border border-dashed bg-muted/20 p-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5" />
                        <span>
                            Set a Main App URL above to enable app mode detection. Overrides are only used
                            when the widget detects it's running in app mode.
                        </span>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
}
